/**
 * Runtime environment accessor.
 *
 * Resolves VITE_* values at RUNTIME (container startup), not build time. Read
 * order:
 *   1. window.__VITE_ENVS — populated by vite-envs.sh from the container env at
 *      startup (standalone, index.html inline script) or seeded by
 *      src/mf-env-init.ts (MFE mode: VITE_APP_ENV + VITE_STANDALONE only).
 *   2. import.meta.env — build-time / dev fallback. (vite-envs also rewrites
 *      these to window.__VITE_ENVS lookups, so this is mainly the dev path and
 *      the MFE path for keys mf-env-init.ts doesn't seed.)
 *
 * Read LAZILY — call getEnv()/readEnv() at use time, never at module top level,
 * so window.__VITE_ENVS is already injected before the first read.
 */

type WindowWithViteEnvs = Window & { __VITE_ENVS?: Record<string, string> }

/** Raw string lookup with the runtime-first fallback chain. */
export function readEnv(key: string): string | undefined {
  const win = (
    typeof window !== 'undefined' ? window : null
  ) as WindowWithViteEnvs | null
  const fromWindow = win?.__VITE_ENVS?.[key]
  if (fromWindow !== undefined && fromWindow !== '') return fromWindow
  return (import.meta.env as unknown as Record<string, string | undefined>)[key]
}

/**
 * A value is truthy only when the env string is exactly "true".
 *
 * `fallback` applies when the var is unset or empty, so an opt-OUT kill switch
 * can default to on. Existing opt-IN flags keep the default `false`.
 */
const asBool = (value: string | undefined, fallback = false): boolean =>
  value === undefined || value === '' ? fallback : value === 'true'

/** Parse a numeric env value, falling back to `fallback` when unset/invalid. */
const asNumber = (value: string | undefined, fallback: number): number => {
  const parsed = Number(value)
  return Number.isNaN(parsed) ? fallback : parsed
}

/**
 * Typed snapshot of the app config, resolved from the runtime env at call time.
 * Keys mirror the VITE_* vars declared in .env.example.
 */
export function getEnv() {
  return {
    APP_ENV: readEnv('VITE_APP_ENV'),
    BASEURL: readEnv('VITE_BASEURL'),
    MOTOR_BASE_URL: readEnv('VITE_MOTOR_BASE_URL'),

    // Auth / OIDC
    CLIENT_SECRET: readEnv('VITE_CLIENT_SECRET'),
    IDENTITY_CLIENT_ID: readEnv('VITE_IDENTITY_CLIENT_ID'),
    IDENTITY_AUTHORITY_URL: readEnv('VITE_IDENTITY_AUTHORITY_URL'),
    IDENTITY_REDIRECT_URI: readEnv('VITE_IDENTITY_REDIRECT_URI'),

    // Ameen Chat
    AMEEN_URL: readEnv('VITE_AMEEN_URL'),

    // Google Tag Manager
    GTM_ID: readEnv('VITE_GTM_ID'),
    GTM_ENABLED: asBool(readEnv('VITE_GTM_ENABLED')),

    // WebEngage
    WEBENGAGE_ID: readEnv('VITE_WEBENGAGE_ID'),
    WEBENGAGE_ENABLED: asBool(readEnv('VITE_WEBENGAGE_ENABLED')),

    // Quotations
    QUOTATIONS_REFETCH_INTERVAL_IN_SECONDS: asNumber(
      readEnv('VITE_QUOTATIONS_REFETCH_INTERVAL_IN_SECONDS'),
      60
    ),
    FILTER_MIN_QUOTATIONS: asNumber(readEnv('VITE_FILTER_MIN_QUOTATIONS'), 4),

    // IC special offers — kill switch for the promo labels in the quote list.
    // Defaults to ON so an environment that never sets it keeps showing the
    // offers configured in features/quote-list/config/special-offers.ts.
    IC_SPECIAL_OFFERS_ENABLED: asBool(
      readEnv('VITE_IC_SPECIAL_OFFERS_ENABLED'),
      true
    ),
  }
}
